// Home: "Since you started with Magically Linked" — follower growth since the first scan, DMs and reply
// rate, and the hours Magically Linked has saved, priced at what the user says their hour is worth.
// One LinkedIn account, or all of them added up. Numbers: GET /api/analytics/impact.

import type { IgAccount } from "@ml/shared";
import {
  DEFAULT_HOURLY_RATE,
  ENGAGEMENT_MIN_BEFORE,
  HOURLY_RATE_PRESETS,
  MAX_HOURLY_RATE,
  MIN_HOURLY_RATE,
  PHONE_WORDS_PER_MINUTE,
  TIME_SAVED_TASKS,
  timeValue,
  type ImpactAccountSummary,
  type ImpactReport,
} from "@ml/shared/features/impact";
import { Avatar, Button, Callout, Field, Input, Modal, SegmentedControl, Skeleton, Table, cx, useToast } from "@ml/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Clock, DollarSign, MessageCircle, Pencil, Sparkles, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatDate, formatNumber } from "../../../lib/format";
import { useIgAccount } from "../../../lib/igAccount";
import { analyticsApi, analyticsKeys } from "../api";
import { daysText, duration, durationText, each, growthFigure, money, mood, pct, signed, worth } from "./format";
import "./impact.css";

type Scope = "account" | "all";
const SCOPE_KEY = "gf.impactScope";

function readScope(): Scope {
  try {
    return localStorage.getItem(SCOPE_KEY) === "all" ? "all" : "account";
  } catch {
    return "account";
  }
}

export function ImpactPanel({ account }: { account: IgAccount }) {
  const { accounts, setCurrent } = useIgAccount();
  const many = accounts.length > 1;
  const [scopePref, setScopePref] = useState<Scope>(readScope);
  const scope: Scope = many ? scopePref : "account";
  const chooseScope = (s: Scope) => {
    setScopePref(s);
    try {
      localStorage.setItem(SCOPE_KEY, s);
    } catch {
      /* private window: the choice lasts for this visit */
    }
  };
  const target = scope === "all" ? null : account.id;
  const q = useQuery({
    queryKey: analyticsKeys.impact(target),
    queryFn: () => analyticsApi.impact(target),
    refetchInterval: 5 * 60_000,
    placeholderData: (prev) => (prev && (prev.scope === "all") === (scope === "all") ? prev : undefined),
  });
  const [rateOpen, setRateOpen] = useState(false);
  const r = q.data;

  return (
    <section className={cx("im", r && `im--${mood(r)}`)} aria-labelledby="im-title" aria-busy={q.isPending}>
      <div className="im__glow" aria-hidden="true" />
      <header className="im__head">
        <p className="im__eyebrow">
          <Sparkles size={14} aria-hidden="true" /> Since you started with Magically Linked
        </p>
        {many ? (
          <SegmentedControl
            label="Show results for"
            size="sm"
            value={scope}
            onChange={chooseScope}
            options={[
              { value: "account", label: `@${account.username}` },
              { value: "all", label: `All ${accounts.length} accounts` },
            ]}
          />
        ) : null}
      </header>

      {q.isPending ? (
        <div className="im__loading">
          <Skeleton height={34} width="70%" />
          <Skeleton height={16} width="40%" />
          <div className="im__tiles">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={118} radius={10} />
            ))}
          </div>
        </div>
      ) : !r ? (
        <Callout tone="danger" title="Couldn't load your results" action={<Button size="sm" onClick={() => void q.refetch()}>Try again</Button>}>
          {q.error instanceof Error ? q.error.message : "Please try again."}
        </Callout>
      ) : (
        <ImpactBody
          r={r}
          onEditRate={() => setRateOpen(true)}
          onPickAccount={(id) => {
            setCurrent(id);
            chooseScope("account");
          }}
        />
      )}

      <RateModal open={rateOpen} onClose={() => setRateOpen(false)} current={r?.hourlyRate ?? DEFAULT_HOURLY_RATE} />
    </section>
  );
}

function ImpactBody({ r, onEditRate, onPickAccount }: { r: ImpactReport; onEditRate: () => void; onPickAccount: (id: string) => void }) {
  const all = r.scope === "all";
  const g = r.growth;
  const growth = growthFigure(g);
  const saved = r.time.totalSeconds;
  const value = timeValue(saved, r.hourlyRate);
  const m = mood(r);
  const who = all ? `${r.accountCount} accounts` : `@${r.accounts[0]?.username ?? ""}`;
  const since = r.startedAt ? formatDate(r.startedAt, { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <>
      <h2 id="im-title" className="im__headline">
        <Headline r={r} growth={growth} saved={saved} value={value} />
      </h2>
      <p className="im__sub">
        {since ? (
          <>
            {who} · with Magically Linked since {since} ({daysText(r.daysUsing)})
            {r.firstScanAt ? <> · first scan {formatDate(r.firstScanAt, { month: "short", day: "numeric" })}</> : null}
          </>
        ) : (
          who
        )}
      </p>

      <div className="im__tiles">
        <Tile
          icon={<TrendingUp />}
          label="Follower growth"
          tone={g.followerGrowth !== null && g.followerGrowth > 0 ? "up" : undefined}
          value={growth ?? "—"}
          animate={g.followerGrowthPct ?? g.followerGrowth}
          format={(v) => (g.followerGrowthPct !== null ? pct(v) : signed(Math.round(v)))}
          foot={
            g.then.followers !== null && g.now.followers !== null ? (
              <>
                {formatNumber(g.then.followers)} → <strong>{formatNumber(g.now.followers)}</strong> followers
                {g.followersPerDay !== null && g.followersPerDay > 0 ? <span className="im__pill">+{g.followersPerDay}/day</span> : null}
              </>
            ) : g.now.followers !== null ? (
              <>{formatNumber(g.now.followers)} followers today — growth counts from your first scan</>
            ) : (
              <>Starts at your first scan</>
            )
          }
        />
        <Tile
          icon={<Clock />}
          label="Time saved"
          value={`${duration(saved).value} ${duration(saved).unit}`}
          animate={saved >= 3600 ? saved / 3600 : null}
          format={(v) => `${v < 10 ? (Math.round(v * 10) / 10).toLocaleString("en-US") : Math.round(v).toLocaleString("en-US")} hours`}
          foot={
            saved > 0 ? (
              <>
                {formatNumber(r.time.actions)} tasks done for you
                {r.time.paceSecondsPer30d ? <span className="im__pill">~{durationText(r.time.paceSecondsPer30d)}/mo</span> : null}
              </>
            ) : (
              <>Counts every follow, DM, comment and post Magically Linked does for you</>
            )
          }
        />
        <Tile
          icon={<DollarSign />}
          label="What that time is worth"
          highlight
          value={money(value)}
          animate={value >= 10 ? value : null}
          format={(v) => money(Math.round(v))}
          foot={
            <>
              at {money(r.hourlyRate)}/hour
              <button type="button" className="im__rate" onClick={onEditRate}>
                <Pencil size={12} aria-hidden="true" />
                {r.isDefaultRate ? "What's your hour worth?" : "Change"}
              </button>
            </>
          }
        />
        <Tile
          icon={<MessageCircle />}
          label="Conversations started"
          value={`${formatNumber(r.messages.sent)} ${r.messages.sent === 1 ? "DM" : "DMs"}`}
          foot={
            r.messages.peopleMessaged > 0 ? (
              <>
                {r.messages.replyRate !== null ? <strong>{pct(r.messages.replyRate).replace("+", "")}</strong> : "—"} reply rate · {formatNumber(r.messages.replied)} of{" "}
                {formatNumber(r.messages.peopleMessaged)} wrote back
              </>
            ) : (
              <>{r.messages.sent > 0 ? "Reply rate shows once replies come in" : "Reply rate shows once your DMs go out"}</>
            )
          }
        />
      </div>

      {m === "fresh" ? (
        <p className="im__fresh">
          Magically Linked locks in where you're starting from on its first scan, then keeps score: how much you've grown, every hour it's saved you, and what
          that time is worth. Keep the extension running and check back tomorrow.
        </p>
      ) : all ? (
        <AccountsTable r={r} onPick={onPickAccount} />
      ) : (
        <ThenNow r={r} />
      )}

      {r.time.lines.length ? <Breakdown r={r} /> : null}
    </>
  );
}

function Headline({ r, growth, saved, value }: { r: ImpactReport; growth: string | null; saved: number; value: number }) {
  const m = mood(r);
  const hours = durationText(saved);
  const worthIt = value >= 1 ? (
    <>
      {" "}— <em className="im__em im__em--money">{money(value)}</em> of your time
    </>
  ) : null;
  if (m === "growing")
    return (
      <>
        You've grown <em className="im__em">{growth}</em>
        {saved > 0 ? (
          <>
            {" "}and Magically Linked saved you <em className="im__em">{hours}</em>
            {worthIt}.
          </>
        ) : (
          <> since your first scan.</>
        )}
      </>
    );
  if (m === "saving")
    return (
      <>
        Magically Linked has saved you <em className="im__em">{hours}</em>
        {worthIt}.
      </>
    );
  if (m === "flat") return <>Your starting line is locked in. Now we keep score.</>;
  return <>Your scoreboard starts now.</>;
}

function Tile({
  icon,
  label,
  value,
  animate,
  format,
  foot,
  highlight,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  /** Numeric target to count up to on first show (null = just show `value`). */
  animate?: number | null;
  format?: (v: number) => string;
  foot: ReactNode;
  highlight?: boolean;
  tone?: "up";
}) {
  const shown = useCountUp(animate ?? null);
  return (
    <div className={cx("im-tile", highlight && "im-tile--money", tone === "up" && "im-tile--up")}>
      <div className="im-tile__top">
        <span className="im-tile__label">{label}</span>
        <span className="im-tile__icon" aria-hidden="true">
          {icon}
        </span>
      </div>
      <div className="im-tile__value">
        {/* Screen readers get the final figure, not every animation frame. */}
        <span aria-hidden="true">{shown !== null && format ? format(shown) : value}</span>
        <span className="gf-sr-only">{value}</span>
      </div>
      <div className="im-tile__foot">{foot}</div>
    </div>
  );
}

/** Ease a number up to its target the first time it shows (and between values after). */
function useCountUp(target: number | null, ms = 1100): number | null {
  const [v, setV] = useState<number | null>(target === null ? null : 0);
  const from = useRef(0);
  useEffect(() => {
    if (target === null) {
      setV(null);
      return;
    }
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const start = from.current;
    // Background tabs pause requestAnimationFrame: show the real number instead of a frozen 0.
    if (reduce || start === target || document.hidden) {
      from.current = target;
      setV(target);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - k, 3);
      const cur = start + (target - start) * eased;
      setV(cur);
      if (k < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      from.current = target;
    };
  }, [target, ms]);
  return v;
}

function ThenNow({ r }: { r: ImpactReport }) {
  const g = r.growth;
  const e = r.engagement;
  if (g.then.followers === null) return null;
  const rows: { label: string; then: number | null; now: number | null; note?: string; good?: "up" | "down" }[] = [
    { label: "Followers", then: g.then.followers, now: g.now.followers, good: "up" },
    { label: "Following", then: g.then.following, now: g.now.following },
    { label: "Posts", then: g.then.posts, now: g.now.posts, good: "up" },
  ];
  return (
    <div className="im-then">
      <div className="im-then__head">
        <span>
          First scan
          {g.then.at ? <small>{formatDate(g.then.at, { month: "short", day: "numeric" })}</small> : null}
        </span>
        <span>Now</span>
        <span>Change</span>
      </div>
      {rows.map((row) => {
        const d = row.then !== null && row.now !== null ? row.now - row.then : null;
        return (
          <div className="im-then__row" key={row.label}>
            <span className="im-then__label">{row.label}</span>
            <span>{formatNumber(row.then)}</span>
            <span className="im-then__now">{formatNumber(row.now)}</span>
            <span className={cx("im-then__delta", d !== null && d !== 0 && row.good && ((d > 0) === (row.good === "up") ? "is-good" : "is-bad"))}>
              {d === null ? "—" : signed(d)}
              {d !== null && row.then ? <small> ({pct(d / row.then)})</small> : null}
            </span>
          </div>
        );
      })}
      {e && (e.beforePosts > 0 || e.sincePosts > 0) ? (
        <div className="im-then__row">
          <span className="im-then__label">
            Likes + comments per post
            <small>
              {e.beforePosts ? `Your last ${e.beforePosts} posts before vs ${e.sincePosts} since` : `${e.sincePosts} posts since you started`}
            </small>
          </span>
          <span>{e.before === null ? "—" : formatNumber(Math.round(e.before))}</span>
          <span className="im-then__now">{e.since === null ? "—" : formatNumber(Math.round(e.since))}</span>
          <span className={cx("im-then__delta", e.liftPct !== null && e.liftPct !== 0 && (e.liftPct > 0 ? "is-good" : "is-bad"))}>
            {e.liftPct !== null ? pct(e.liftPct) : <small>{e.beforePosts < ENGAGEMENT_MIN_BEFORE ? "Nothing earlier to compare" : "Collecting"}</small>}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function AccountsTable({ r, onPick }: { r: ImpactReport; onPick: (id: string) => void }) {
  return (
    <div className="im-accounts">
      <Table<ImpactAccountSummary>
        caption="Results per LinkedIn account"
        density="compact"
        rows={r.accounts}
        rowKey={(a) => a.igAccountId}
        onRowClick={(a) => onPick(a.igAccountId)}
        defaultSort={{ key: "time", dir: "desc" }}
        columns={[
          {
            key: "account",
            header: "Account",
            cell: (a) => (
              <span className="im-accounts__who">
                <Avatar src={a.profilePicUrl} name={a.fullName || a.username} size={26} />@{a.username}
              </span>
            ),
            sortValue: (a) => a.username,
          },
          { key: "time", header: "Time saved", align: "right", cell: (a) => durationText(a.timeSavedSeconds), sortValue: (a) => a.timeSavedSeconds },
          { key: "worth", header: "Worth", align: "right", cell: (a) => <strong>{worth(a.timeSavedSeconds, r.hourlyRate)}</strong>, sortValue: (a) => a.timeSavedSeconds },
          {
            key: "growth",
            header: "Growth",
            align: "right",
            cell: (a) => {
              const f = growthFigure(a.growth);
              return f ? <span className={cx((a.growth.followerGrowth ?? 0) > 0 && "im-good")}>{f}</span> : <span className="im-muted">—</span>;
            },
            sortValue: (a) => a.growth.followerGrowthPct ?? a.growth.followerGrowth,
          },
          {
            key: "followers",
            header: "Followers",
            align: "right",
            className: "im-accounts__opt",
            cell: (a) =>
              a.growth.then.followers === null ? (
                <span className="im-muted">{formatNumber(a.growth.now.followers)} · no scan yet</span>
              ) : (
                <>
                  {formatNumber(a.growth.then.followers)} → <strong>{formatNumber(a.growth.now.followers)}</strong>
                </>
              ),
            sortValue: (a) => a.growth.now.followers,
          },
          { key: "dms", header: "DMs", align: "right", className: "im-accounts__opt", cell: (a) => formatNumber(a.messagesSent), sortValue: (a) => a.messagesSent },
        ]}
      />
    </div>
  );
}

function Breakdown({ r }: { r: ImpactReport }) {
  return (
    <details className="im-how">
      <summary>
        How we count your time
        <ChevronDown size={15} aria-hidden="true" />
      </summary>
      <p className="im-how__lead">
        Every task below is something Magically Linked did for you, timed at how long it takes a person doing the same work by hand on their phone. Messages are
        timed from the words Magically Linked actually wrote, typed at {PHONE_WORDS_PER_MINUTE} words a minute (the average phone typing speed in a 37,000-person
        study), plus the reading and deciding before you type. Switching apps and getting distracted aren't counted, so the real number is bigger.
      </p>
      <table className="im-how__table">
        <caption className="gf-sr-only">Time saved by task</caption>
        <thead>
          <tr>
            <th scope="col">Done for you</th>
            <th scope="col">Count</th>
            <th scope="col">By hand, each</th>
            <th scope="col">Time saved</th>
          </tr>
        </thead>
        <tbody>
          {r.time.lines.map((l) => {
            const t = TIME_SAVED_TASKS[l.task];
            return (
              <tr key={l.task}>
                <th scope="row">
                  {t.label}
                  <small>{t.byHand}</small>
                  {l.words ? (
                    <small className="im-how__words">
                      {formatNumber(l.words)} {l.words === 1 ? "word" : "words"} typed
                      {l.personal ? <> · {formatNumber(l.personal)} written from their profile</> : null}
                    </small>
                  ) : null}
                </th>
                <td>{formatNumber(l.count)}</td>
                <td>{each(Math.round(l.seconds / l.count))}</td>
                <td>{durationText(l.seconds)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td>{formatNumber(r.time.actions)}</td>
            <td />
            <td>
              {durationText(r.time.totalSeconds)} · <strong>{worth(r.time.totalSeconds, r.hourlyRate)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </details>
  );
}

function RateModal({ open, onClose, current }: { open: boolean; onClose: () => void; current: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState(String(current));
  useEffect(() => {
    if (open) setText(String(current));
  }, [open, current]);
  const n = Number(text.replace(/[$,\s]/g, ""));
  const valid = Number.isInteger(n) && n >= MIN_HOURLY_RATE && n <= MAX_HOURLY_RATE;

  const save = useMutation({
    mutationFn: (rate: number) => analyticsApi.setHourlyRate(rate),
    onSuccess: ({ hourlyRate }) => {
      // Every scope reprices instantly; the server's copy follows.
      qc.setQueriesData<ImpactReport>({ queryKey: analyticsKeys.impactAll }, (old) => (old ? { ...old, hourlyRate, isDefaultRate: false } : old));
      void qc.invalidateQueries({ queryKey: analyticsKeys.impactAll });
      toast.success(`Your time is now valued at ${money(hourlyRate)} an hour.`);
      onClose();
    },
    onError: (err) => toast.error(err),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="What's an hour of your time worth?"
      description="We use it to put a dollar figure on the time Magically Linked saves you. For online business owners and creators, $25 an hour is the floor — most are worth a lot more."
      onSubmit={() => valid && save.mutate(n)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={save.isPending} disabled={!valid}>
            Save
          </Button>
        </>
      }
    >
      <div className="im-rate__presets" role="group" aria-label="Quick picks">
        {HOURLY_RATE_PRESETS.map((p) => (
          <button key={p} type="button" className={cx("im-rate__chip", n === p && "is-on")} aria-pressed={n === p} onClick={() => setText(String(p))}>
            {money(p)}
          </button>
        ))}
      </div>
      <Field label="Your hourly rate" hint={`Whole dollars, ${formatNumber(MIN_HOURLY_RATE)} to ${formatNumber(MAX_HOURLY_RATE)}.`} error={text && !valid ? "Enter a whole dollar amount." : undefined}>
        <Input
          inputMode="numeric"
          icon={<DollarSign size={15} />}
          suffix="/ hour"
          value={text}
          invalid={!!text && !valid}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
      </Field>
    </Modal>
  );
}
